export const PlacementAttributeName = 'data-popper-placement';
import * as TypeStyle from 'typestyle';
import { NestedCSSSelectors } from 'typestyle/lib/types';
import { TabStyles } from '../../styles/TabStyles';
import { TextEllipsis } from '../../styles/TextEllipsis';
import { ColorMap } from '../AlertStyles';
import { getStyles as getDialogHeaderStyles } from '../Dialog/DialogHeader';
import { DialogBorderWidth, getStyles as getDialogStyles } from '../Dialog/DialogWidget';

const BorderWidth = DialogBorderWidth;
const MaxBodyHeight = 316;
const PopoverBoxShadow: React.CSSProperties['boxShadow'] = '0 10px 16px -4px rgba(42, 87, 117, 0.20)';

export const PopoverStyleName = TypeStyle.style(
  {
    ...getDialogStyles().Dialog,
    borderRadius: 2,
    boxShadow: PopoverBoxShadow,
    boxSizing: 'border-box',
    color: TabStyles.Typography.Color.Primary,
    fontSize: TabStyles.Typography.Sizing.Default,
    position: 'absolute',
    $nest: {
      // add 4px affordance to allow clicking on the scrollbar without causing the popover to close
      '&.titleBarVisible::after': {
        bottom: 0,
        content: `""`,
        position: 'absolute',
        right: -4,
        top: 0,
        width: 4,
      },
      '&.darkMode': {
        color: TabStyles.Typography.Color.InvertedPrimary,
      }
    }
  }
);

export const PopoverTriggerStyleName = TypeStyle.style({
  $nest: {
    // By default [aria-haspopup] will add a dropdown arrow to the button. Hide that for our trigger.
    '&[aria-haspopup]::after': {
      display: 'none !important',
    },
  }
});

export const PopoverAlertStyles: NestedCSSSelectors = {
  '&.success': { backgroundColor: ColorMap.success.backgroundColor },
  '&.info': { backgroundColor: ColorMap.info.backgroundColor },
  '&.warning': { backgroundColor: ColorMap.warning.backgroundColor },
  '&.error': { backgroundColor: ColorMap.error.backgroundColor },
};

export const TitleBarStyleName = TypeStyle.style({
  ...getDialogHeaderStyles().TitleBar,
  height: 40,
  fontSize: 14,
  paddingRight: 40, // account for close button
  $nest: {
    ...PopoverAlertStyles,
    '> div': { // used to vertically align the StatusIcon and the titlebar text
      display: 'inline-flex',
    },
    '> span': { // truncation doesn't work correctly on a display: flex element, so wrap in a span
      ...TextEllipsis
    },
  },
});

export const CloseButtonStyle: React.CSSProperties = {
  height: 24,
  position: 'absolute',
  right: 8,
  top: 8,
  width: 24,
};

export const BodyStyleName = TypeStyle.style({
  marginTop: 16,
  marginRight: 40,
  marginBottom: 16,
  maxHeight: MaxBodyHeight,
  overflowY: 'auto',
  paddingRight: 4,
  paddingLeft: 16,
  $nest: {
    '&.titleBarVisible': {
      marginRight: 0,
      marginTop: 0,
      minHeight: 40, // avoid a nubbin which spans both the header and body
      paddingRight: 16,
      paddingTop: 16,
    },
  },
});

export const FooterStyleName = TypeStyle.style({
  boxSizing: 'border-box',
  display: 'flex',
  height: 40,
  justifyContent: 'flex-end',
  padding: 16,
  paddingTop: 0,
  $nest: {
    '& button': {
      marginLeft: 16,
    },
  }
});

const NubbinSegmentLength = 14; // ~Math.sqrt(8^2 + 11^2) pythagorean theorem to get segment length

const NubbinStyles: React.CSSProperties = {
  background: 'inherit',
  border: 'inherit',
  boxSizing: 'border-box',
  height: NubbinSegmentLength,
  position: 'absolute',
  width: NubbinSegmentLength,
  zIndex: -1,
};

export const NubbinStyleName = TypeStyle.style({
  $debugName: 'nubbin',
  ...NubbinStyles,
  visibility: 'hidden',
  $nest: {
    [`&[${PlacementAttributeName}^="top"]`]: {
      borderLeft: 'none',
      borderTop: 'none',
      bottom: -NubbinSegmentLength / 2 - BorderWidth,
    },
    [`&[${PlacementAttributeName}^="bottom"]`]: {
      borderBottom: 'none',
      borderRight: 'none',
      top: -NubbinSegmentLength / 2 - BorderWidth * 2,
    },
    [`&[${PlacementAttributeName}^="left"]`]: {
      borderBottom: 'none',
      borderLeft: 'none',
      right: -NubbinSegmentLength / 2 - BorderWidth,
    },
    [`&[${PlacementAttributeName}^="right"]`]: {
      borderTop: 'none',
      borderRight: 'none',
      left: -NubbinSegmentLength / 2 - BorderWidth * 2,
    },
    '&::before': {
      ...NubbinStyles,
      backgroundColor: 'inherit',
      content: `""`,
      transform: 'rotate(45deg)',
      visibility: 'visible',
    },
    '&.hidden::before': {
      visibility: 'hidden',
    },
    '&.nubbinHasColor': {
      backgroundColor: getDialogHeaderStyles().TitleBar.backgroundColor,
      $nest: {
        ...PopoverAlertStyles,
      }
    },
  },
});
